// Include the USWDS Accordion script.
// This makes the component available globally. If you're only using it on certain pages,
// include it on a template-specific script file instead.
// Be sure to initialize any components as well (see init() function below.)
import accordion from 'uswds/src/js/components/accordion';
import banner from 'uswds/src/js/components/banner';
import tooltip from 'uswds/src/js/components/tooltip';
import table from 'uswds/src/js/components/table';
import modal from 'uswds/src/js/components/modal';
// Example: Import other USWDS JavaScript components as needed
// import characterCount from 'uswds/src/js/components/character-count';
// import comboBox from 'uswds/src/js/components/combo-box';
// import datePicker from 'uswds/src/js/components/date-picker';
// import fileInput from 'uswds/src/js/components/file-input';
import navigation from 'uswds/src/js/components/navigation';
// import password from 'uswds/src/js/components/password';
// import search from 'uswds/src/js/components/search';
// import skipnav from 'uswds/src/js/components/skipnav';
// import timePicker from 'uswds/src/js/components/time-picker';
// import validator from 'uswds/src/js/components/validator';
import Drupal from 'drupal';

// Prevent USWDS accordion auto-expansion for main menu only
document.addEventListener('DOMContentLoaded', () => {
  // Close any auto-expanded accordions in the main menu only
  const expandedMenuAccordions = document.querySelectorAll('.c-menu--main .usa-accordion__button[aria-expanded="true"]');
  expandedMenuAccordions.forEach(button => {
    button.setAttribute('aria-expanded', 'false');
    const target = document.getElementById(button.getAttribute('aria-controls'));
    if (target) {
      target.setAttribute('hidden', '');
    }
  });
  
  // Initialize navigation component for mobile menu (standalone initialization)
  // This ensures it works even if behaviors don't attach properly
  const usaHeader = document.querySelector('.usa-header');
  if (usaHeader) {
    navigation.on(usaHeader);
  }
});

// Also initialize immediately if DOM is already loaded (for cases where script loads after DOMContentLoaded)
if (document.readyState !== 'loading') {
  const usaHeader = document.querySelector('.usa-header');
  if (usaHeader) {
    navigation.on(usaHeader);
  }
}

const AUTO_MODAL_STORAGE_PREFIX = 'aphisAutoModal::';
const DAY_IN_MS = 24 * 60 * 60 * 1000;
const autoModalQueue = [];
let activeAutoModal = null;
let autoModalQueueSequence = 0;

function parseNonNegativeNumber(value, fallbackValue) {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue >= 0 ? parsedValue : fallbackValue;
}

function parseNumberInRange(value, fallbackValue, minValue, maxValue) {
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) {
    return fallbackValue;
  }

  return Math.min(maxValue, Math.max(minValue, parsedValue));
}

function getAutoModalTargetId(modalElement) {
  const modalWrapper = modalElement.closest('.usa-modal-wrapper');
  if (modalWrapper instanceof HTMLElement && modalWrapper.id) {
    return modalWrapper.id;
  }

  if (modalElement.id) {
    return modalElement.id;
  }

  return null;
}

function getAutoModalStorageKey(modalElement) {
  const modalTargetId = getAutoModalTargetId(modalElement);
  if (!modalTargetId) {
    return null;
  }
  return `${AUTO_MODAL_STORAGE_PREFIX}${modalTargetId}`;
}

function canDisplayAutoModal(modalElement) {
  const storageKey = getAutoModalStorageKey(modalElement);
  if (!storageKey) {
    return true;
  }

  const frequency = modalElement.dataset.autoModalFrequency || 'always';

  try {
    if (frequency === 'session') {
      return !sessionStorage.getItem(storageKey);
    }

    if (frequency === 'days') {
      const days = parseNonNegativeNumber(modalElement.dataset.autoModalDays, 1);
      const lastShownTimestamp = Number(localStorage.getItem(storageKey));

      if (!lastShownTimestamp || !Number.isFinite(lastShownTimestamp)) {
        return true;
      }

      return (Date.now() - lastShownTimestamp) >= (days * DAY_IN_MS);
    }
  } catch (error) {
    // Storage can fail in private browsing modes; fail open for usability.
    return true;
  }

  return true;
}

function markAutoModalAsDisplayed(modalElement) {
  const storageKey = getAutoModalStorageKey(modalElement);
  if (!storageKey) {
    return;
  }

  const frequency = modalElement.dataset.autoModalFrequency || 'always';

  try {
    if (frequency === 'session') {
      sessionStorage.setItem(storageKey, '1');
      return;
    }

    if (frequency === 'days') {
      localStorage.setItem(storageKey, String(Date.now()));
    }
  } catch (error) {
    // Ignore storage write errors.
  }
}

function openAutoModal(modalElement) {
  const modalId = getAutoModalTargetId(modalElement);
  if (!modalId) {
    return;
  }

  if (modalElement.classList.contains('is-visible')) {
    return;
  }

  const existingTrigger = document.querySelector(
    `[data-open-modal][aria-controls="${modalId}"], [data-open-modal][href="#${modalId}"]`
  );

  if (existingTrigger instanceof HTMLElement) {
    existingTrigger.click();
    return;
  }

  const syntheticTrigger = document.createElement('a');
  syntheticTrigger.href = `#${modalId}`;
  syntheticTrigger.setAttribute('aria-controls', modalId);
  syntheticTrigger.setAttribute('data-open-modal', '');
  syntheticTrigger.setAttribute('hidden', 'hidden');
  document.body.appendChild(syntheticTrigger);
  syntheticTrigger.click();
  syntheticTrigger.remove();
}

function getAutoModalWrapper(modalElement) {
  const wrapper = modalElement.closest('.usa-modal-wrapper');
  return wrapper instanceof HTMLElement ? wrapper : null;
}

function processAutoModalQueue() {
  if (activeAutoModal || autoModalQueue.length === 0) {
    return;
  }

  const nextEntry = autoModalQueue.shift();
  const nextModal = nextEntry ? nextEntry.modalElement : null;
  if (!(nextModal instanceof HTMLElement)) {
    return;
  }

  activeAutoModal = nextModal;
  openAutoModal(nextModal);

  const modalWrapper = getAutoModalWrapper(nextModal);
  if (!modalWrapper) {
    activeAutoModal = null;
    processAutoModalQueue();
    return;
  }

  let observer;
  const releaseQueueWhenClosed = () => {
    if (!modalWrapper.classList.contains('is-visible')) {
      activeAutoModal = null;
      observer.disconnect();
      processAutoModalQueue();
    }
  };

  observer = new MutationObserver(releaseQueueWhenClosed);
  observer.observe(modalWrapper, { attributes: true, attributeFilter: ['class'] });
  releaseQueueWhenClosed();
}

function enqueueAutoModal(modalElement, priority = 100) {
  const alreadyQueued = autoModalQueue.some((entry) => entry.modalElement === modalElement);
  if (alreadyQueued || activeAutoModal === modalElement) {
    return;
  }

  const sequence = autoModalQueueSequence;
  autoModalQueueSequence += 1;
  autoModalQueue.push({
    modalElement,
    priority,
    sequence,
  });

  autoModalQueue.sort((a, b) => {
    if (a.priority !== b.priority) {
      return a.priority - b.priority;
    }
    return a.sequence - b.sequence;
  });

  processAutoModalQueue();
}

function attachAutoModalTriggers(context, settings = {}) {
  if (settings.isAdminPage) {
    return;
  }

  const modals = context.querySelectorAll('.usa-modal[data-auto-modal="true"]');

  modals.forEach((modalElement) => {
    if (!(modalElement instanceof HTMLElement)) {
      return;
    }

    if (modalElement.hasAttribute('data-auto-modal-initialized')) {
      return;
    }
    modalElement.setAttribute('data-auto-modal-initialized', 'true');

    if (!canDisplayAutoModal(modalElement)) {
      return;
    }

    const openAndTrack = () => {
      markAutoModalAsDisplayed(modalElement);
      enqueueAutoModal(modalElement);
    };

    const triggerType = modalElement.dataset.autoModalTrigger || 'on_load';

    if (triggerType === 'on_load') {
      window.setTimeout(openAndTrack, 0);
      return;
    }

    if (triggerType === 'delay') {
      const delaySeconds = parseNonNegativeNumber(modalElement.dataset.autoModalDelaySeconds, 2);
      window.setTimeout(openAndTrack, delaySeconds * 1000);
      return;
    }

    if (triggerType === 'scroll') {
      const scrollPercentThreshold = parseNumberInRange(
        modalElement.dataset.autoModalScrollPercent,
        50,
        1,
        100
      );

      const onScroll = () => {
        const totalScrollableDistance = document.documentElement.scrollHeight - window.innerHeight;
        const currentScrollPercent = totalScrollableDistance <= 0
          ? 100
          : (window.scrollY / totalScrollableDistance) * 100;

        if (currentScrollPercent >= scrollPercentThreshold) {
          window.removeEventListener('scroll', onScroll);
          markAutoModalAsDisplayed(modalElement);
          enqueueAutoModal(modalElement, scrollPercentThreshold);
        }
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
      return;
    }

    if (triggerType === 'exit_intent') {
      const supportsMouseExitIntent = window.matchMedia
        && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

      if (!supportsMouseExitIntent) {
        return;
      }

      const behaviorAttachedAt = Date.now();
      let hasSeenMouseMovement = false;

      const onMouseMove = (event) => {
        if (event.clientY > 0 && event.clientY < window.innerHeight) {
          hasSeenMouseMovement = true;
          document.removeEventListener('mousemove', onMouseMove);
        }
      };

      const onMouseOut = (event) => {
        if (!hasSeenMouseMovement) {
          return;
        }

        // Prevent false positives immediately after attach.
        if (Date.now() - behaviorAttachedAt < 1000) {
          return;
        }

        const nextTarget = event.relatedTarget;
        const movedTowardBrowserChrome = event.clientY <= 0;
        const leftDocument = !nextTarget;

        if (movedTowardBrowserChrome && leftDocument) {
          document.removeEventListener('mousemove', onMouseMove);
          document.removeEventListener('mouseout', onMouseOut);
          openAndTrack();
        }
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseout', onMouseOut);
    }
  });
}

function gessoUswdsNavigation (context) {
  // Only run on desktop - mobile uses USWDS navigation component
  const isDesktop = window.matchMedia && window.matchMedia('(min-width: 1024px)').matches;
  if (!isDesktop) {
    return;
  }
  
  // Only target subnavs within the main menu, not campaign menus
  const subnav = context.querySelectorAll('.c-menu--main .c-menu__subnav');
  
  // Track which menu is currently open
  let currentlyOpenMenu = null;
  
  // Ensure all subnavs start in a closed state
  subnav.forEach(menu => {
    menu.setAttribute('hidden', '');
  });
  
  subnav.forEach((menu, index) => {
    const button = menu.previousElementSibling;
    if (button && button.classList.contains('usa-nav__link')) {
      const id = `extended-nav-section-${index}`;
      menu.id = id;
      button.setAttribute('aria-controls', id);
      
      // Remove accordion class to prevent USWDS interference (main menu only)
      if (button.classList.contains('usa-accordion__button') && button.closest('.c-menu--main')) {
        button.classList.remove('usa-accordion__button');
      }
      
      // Check if we've already attached a handler to this button
      if (button.hasAttribute('data-menu-handler-attached')) {
        return;
      }
      
      // Mark this button as having a handler attached
      button.setAttribute('data-menu-handler-attached', 'true');
      
      // Ensure the menu starts in a closed state
      button.setAttribute('aria-expanded', 'false');
      menu.setAttribute('hidden', '');
      
      // Function to close a specific menu
      const closeMenu = (menuButton, menuElement) => {
        menuButton.setAttribute('aria-expanded', 'false');
        
        // Hide outer container
        menuElement.setAttribute('hidden', '');
        menuElement.style.display = 'none';
        
        // Hide inner subnav ul elements
        const innerSubnavs = menuElement.querySelectorAll('ul.c-menu__subnav');
        
        innerSubnavs.forEach((innerSubnav) => {
          innerSubnav.setAttribute('hidden', '');
          innerSubnav.style.display = 'none';
        });
        
        menuButton.classList.remove('menu-expanded');
      };
      
      // Function to open a specific menu
      const openMenu = (menuButton, menuElement) => {
        menuButton.setAttribute('aria-expanded', 'true');
        
        // Remove hidden from outer container
        menuElement.removeAttribute('hidden');
        menuElement.style.display = 'block';
        menuElement.style.visibility = 'visible';
        
        // Find and remove hidden from inner subnav ul elements
        const innerSubnavs = menuElement.querySelectorAll('ul.c-menu__subnav');
        
        innerSubnavs.forEach((innerSubnav) => {
          innerSubnav.removeAttribute('hidden');
          innerSubnav.style.display = 'block';
          innerSubnav.style.visibility = 'visible';
        });
        
        // Use setProperty with !important to override any CSS
        menuElement.style.setProperty('display', 'block', 'important');
        menuElement.style.setProperty('visibility', 'visible', 'important');
        menuElement.style.setProperty('opacity', '1', 'important');
        
        menuButton.classList.add('menu-expanded');
      };
      
      // Add click handler to manually toggle accordion state
      button.addEventListener('click', () => {
        const targetSubnav = document.getElementById(button.getAttribute('aria-controls'));
        const isExpanded = button.getAttribute('aria-expanded') === 'true';
        
        if (targetSubnav) {
          if (isExpanded) {
            // Close this menu
            closeMenu(button, targetSubnav);
            currentlyOpenMenu = null;
          } else {
            // Close any other open menu first
            if (currentlyOpenMenu && currentlyOpenMenu !== button) {
              const otherSubnav = document.getElementById(currentlyOpenMenu.getAttribute('aria-controls'));
              if (otherSubnav) {
                closeMenu(currentlyOpenMenu, otherSubnav);
              }
            }
            
            // Open this menu
            openMenu(button, targetSubnav);
            currentlyOpenMenu = button;
          }
        }
      });
      
      // Monitor for changes to the subnav that might be caused by other scripts
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'hidden') {
            // If the menu is supposed to be open but hidden attribute was added back, remove it
            if (menu.hasAttribute('data-menu-open') && menu.hasAttribute('hidden')) {
              menu.removeAttribute('hidden');
              menu.style.display = 'block';
            }
          }
        });
      });
      
      observer.observe(menu, {
        attributes: true,
        attributeFilter: ['hidden', 'style', 'class']
      });
    }
  });
  
  // Function to close all open menus
  const closeAllMenus = () => {
    if (currentlyOpenMenu) {
      const targetSubnav = document.getElementById(currentlyOpenMenu.getAttribute('aria-controls'));
      if (targetSubnav) {
        // Close the menu using the same logic as the closeMenu function
        currentlyOpenMenu.setAttribute('aria-expanded', 'false');
        
        // Hide outer container
        targetSubnav.setAttribute('hidden', '');
        targetSubnav.style.display = 'none';
        
        // Hide inner subnav ul elements
        const innerSubnavs = targetSubnav.querySelectorAll('ul.c-menu__subnav');
        
        innerSubnavs.forEach((innerSubnav) => {
          innerSubnav.setAttribute('hidden', '');
          innerSubnav.style.display = 'none';
        });
        
        currentlyOpenMenu.classList.remove('menu-expanded');
        currentlyOpenMenu = null;
      }
    }
  };
  
  // Add click-outside handler to close menu when clicking elsewhere (desktop only)
  document.addEventListener('click', (event) => {
    // Only handle on desktop
    const isDesktopViewport = window.matchMedia && window.matchMedia('(min-width: 1024px)').matches;
    if (!isDesktopViewport) {
      return;
    }
    
    // Check if the click is outside the main menu area
    const mainMenu = document.querySelector('.c-menu--main');
    if (mainMenu && !mainMenu.contains(event.target)) {
      closeAllMenus();
    }
  });
}

Drupal.behaviors.uswds = {
  attach(context, settings) {
    const initialElem = context === document ? document.body : context;
    
    // Initialize banner first to ensure it works properly
    banner.on(initialElem);
    
    // Initialize accordion component for non-menu accordions
    accordion.on(initialElem);
    
    // Initialize navigation component for main navigation (handles mobile menu toggle)
    // This needs to be initialized on the header or entire context, not just .usa-nav,
    // so it can find and handle the .usa-menu-btn button
    // Note: Standalone initialization also happens outside behaviors to ensure it works
    const usaHeader = context.querySelector('.usa-header');
    if (usaHeader) {
      navigation.on(usaHeader);
    } else {
      // Fallback: initialize on the entire context if header not found
      navigation.on(initialElem);
    }
    
    // Disable USWDS accordion components only for desktop main menu to prevent conflicts
    // But exclude banner buttons and other accordion components like tabs
    // Only remove on desktop (tablet-lg and above) to preserve mobile functionality
    const mainMenuAccordionButtons = context.querySelectorAll('.c-menu--main .usa-accordion__button:not(.usa-banner__button)');
    mainMenuAccordionButtons.forEach(button => {
      // Only remove USWDS accordion classes on desktop
      // Mobile menu needs these classes for USWDS navigation component
      if (window.matchMedia && window.matchMedia('(min-width: 1024px)').matches) {
        button.classList.remove('usa-accordion__button');
        button.removeAttribute('aria-expanded');
        button.removeAttribute('aria-controls');
      }
    });
    
    // Initialize our custom navigation (for desktop menu dropdowns)
    gessoUswdsNavigation(context);
    
    // Mobile menu submenu handling - ensure accordion buttons work and menu closes on link clicks
    function handleMobileMenuSubmenus() {
      const isMobile = window.matchMedia && !window.matchMedia('(min-width: 1024px)').matches;
      if (!isMobile) {
        return;
      }
      
      const mainMenu = context.querySelector('.c-menu--main');
      if (!mainMenu) {
        return;
      }
      
      // Don't initialize USWDS accordion component for mobile menu submenus
      // We'll handle it manually to ensure it works properly on mobile
      // The accordion component can interfere with mobile touch events
      
      // Handle submenu accordion buttons - ensure they toggle properly on mobile
      // Manually handle toggle to ensure it works reliably on mobile devices
      const accordionButtons = mainMenu.querySelectorAll('.usa-accordion__button');
      accordionButtons.forEach((button, index) => {
        // Skip if already handled
        if (button.hasAttribute('data-mobile-handler-attached')) {
          return;
        }
        button.setAttribute('data-mobile-handler-attached', 'true');
        
        // Ensure aria-controls is set - find the next sibling subnav
        let ariaControls = button.getAttribute('aria-controls');
        let subnav = null;
        
        if (ariaControls) {
          subnav = document.getElementById(ariaControls);
        }
        
        // If no aria-controls or subnav not found, find it by looking for next sibling
        if (!subnav) {
          // Find the next sibling that is a .c-menu__subnav
          let nextSibling = button.nextElementSibling;
          while (nextSibling) {
            if (nextSibling.classList.contains('c-menu__subnav')) {
              subnav = nextSibling;
              break;
            }
            nextSibling = nextSibling.nextElementSibling;
          }
          
          // If found, set aria-controls and id
          if (subnav) {
            const id = subnav.id || `mobile-nav-section-${index}`;
            if (!subnav.id) {
              subnav.id = id;
            }
            button.setAttribute('aria-controls', id);
            ariaControls = id;
          }
        }
        
        if (!subnav) {
          return;
        }
        
        // Initial state sync - ensure hidden attribute matches aria-expanded
        const initialExpanded = button.getAttribute('aria-expanded') === 'true';
        if (initialExpanded && subnav.hasAttribute('hidden')) {
          subnav.removeAttribute('hidden');
        } else if (!initialExpanded && !subnav.hasAttribute('hidden')) {
          subnav.setAttribute('hidden', '');
        }
        
        // Function to toggle the submenu
        const toggleSubmenu = () => {
          const isExpanded = button.getAttribute('aria-expanded') === 'true';
          
          if (isExpanded) {
            // Currently expanded, so close it
            button.setAttribute('aria-expanded', 'false');
            subnav.setAttribute('hidden', '');
          } else {
            // Currently closed, so open it
            button.setAttribute('aria-expanded', 'true');
            subnav.removeAttribute('hidden');
          }
        };
        
        // Add click handler - handle toggle manually for mobile
        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          toggleSubmenu();
        });
        
        // Also handle touchstart for better mobile responsiveness
        button.addEventListener('touchstart', (event) => {
          event.preventDefault();
          event.stopPropagation();
          toggleSubmenu();
        }, { passive: false });
        
        // Also add a MutationObserver to sync state if USWDS changes aria-expanded
        const observer = new MutationObserver(() => {
          const isExpanded = button.getAttribute('aria-expanded') === 'true';
          if (isExpanded && subnav.hasAttribute('hidden')) {
            subnav.removeAttribute('hidden');
          } else if (!isExpanded && !subnav.hasAttribute('hidden')) {
            subnav.setAttribute('hidden', '');
          }
        });
        
        observer.observe(button, {
          attributes: true,
          attributeFilter: ['aria-expanded']
        });
      });
      
      // Close mobile menu when clicking links inside submenus
      const submenuLinks = mainMenu.querySelectorAll('.c-menu__subnav a');
      submenuLinks.forEach(link => {
        if (link.hasAttribute('data-mobile-close-handler')) {
          return;
        }
        link.setAttribute('data-mobile-close-handler', 'true');
        
        // Close menu when link is clicked, but ensure navigation happens first
        link.addEventListener('click', () => {
          // Don't prevent default or stop propagation - let the link navigate normally
          // Close menu by directly hiding it instead of clicking close button
          // This avoids interfering with navigation
          setTimeout(() => {
            const usaNav = document.querySelector('.usa-nav');
            const menuButton = document.querySelector('.usa-menu-btn');
            if (usaNav && menuButton) {
              // Hide the nav directly
              usaNav.setAttribute('hidden', '');
              // Update menu button state
              menuButton.setAttribute('aria-expanded', 'false');
              // Remove overlay if it exists
              const overlay = document.querySelector('.usa-overlay');
              if (overlay) {
                overlay.classList.remove('is-visible');
              }
            }
          }, 300); // Longer delay to ensure navigation happens
        });
      });
    }
    
    // Initialize mobile menu handlers
    handleMobileMenuSubmenus();
    
    // Re-initialize on window resize in case we switch between mobile/desktop
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        handleMobileMenuSubmenus();
      }, 250);
    });
    
    // Initialize navigation component for campaign menu only
    const campaignMenu = context.querySelector('.c-menu--campaign');
    if (campaignMenu) {
      // Set aria-controls BEFORE initializing navigation component
      // This ensures the component can properly attach event handlers on first click
      const campaignMenuButtons = Array.from(context.querySelectorAll('.c-menu--campaign .usa-accordion__button'));
      const buttonData = [];
      
      campaignMenuButtons.forEach((button, index) => {
        // Always set aria-controls, even if it exists, to ensure it's correct
        // Find the next sibling subnav element
        const subnav = button.nextElementSibling;
        if (subnav && subnav.classList.contains('c-menu__subnav')) {
          const id = subnav.id || `campaign-nav-section-${index}`;
          // Ensure subnav has an ID
          if (!subnav.id) {
            subnav.id = id;
          }
          // Store button data before cloning
          buttonData.push({
            button,
            ariaControls: subnav.id,
            ariaExpanded: button.getAttribute('aria-expanded') || 'false',
            className: button.className
          });
        }
      });
      
      // Remove event listeners that might have been attached by global accordion
      // Clone buttons to remove any event listeners, then restore attributes
      const newButtons = [];
      buttonData.forEach(({ button, ariaControls, ariaExpanded, className }) => {
        const parent = button.parentNode;
        const newButton = button.cloneNode(true);
        // Restore the attributes we need
        newButton.setAttribute('aria-controls', ariaControls);
        newButton.setAttribute('aria-expanded', ariaExpanded);
        newButton.className = className;
        parent.replaceChild(newButton, button);
        newButtons.push({ button: newButton, ariaControls });
      });
      
      // Add manual click handler to handle menu toggles
      // Since we've cleaned up the buttons, we'll handle clicks directly
      newButtons.forEach(({ button, ariaControls }) => {
        button.addEventListener('click', (event) => {
          event.stopPropagation(); // Prevent other handlers from interfering
          const subnav = document.getElementById(ariaControls);
          const currentExpanded = button.getAttribute('aria-expanded');
          const isExpanded = currentExpanded === 'true';
          
          if (subnav) {
            if (isExpanded) {
              // Close
              button.setAttribute('aria-expanded', 'false');
              subnav.setAttribute('hidden', '');
              // Remove inline styles when closing
              subnav.style.display = '';
              subnav.style.visibility = '';
            } else {
              // Open - ensure this happens on first click
              button.setAttribute('aria-expanded', 'true');
              subnav.removeAttribute('hidden');
              // Force display to ensure it's visible
              subnav.style.display = 'block';
              subnav.style.visibility = 'visible';
            }
          }
        }, true); // Use capture phase to run before other handlers
      });
      
      // Don't initialize navigation component on campaign menu
      // Our manual handler handles everything
    }
    
    // Add click-outside functionality for campaign menu
    document.addEventListener('click', (event) => {
      // Don't interfere with clicks on accordion buttons - our manual handler handles them
      if (event.target.closest('.c-menu--campaign .usa-accordion__button')) {
        return;
      }
      
      const campaignMenuElement = document.querySelector('.c-menu--campaign');
      if (campaignMenuElement && !campaignMenuElement.contains(event.target)) {
        // Close any open campaign menu submenus
        const openCampaignButtons = campaignMenuElement.querySelectorAll('.usa-accordion__button[aria-expanded="true"]');
        openCampaignButtons.forEach(button => {
          button.setAttribute('aria-expanded', 'false');
          const target = document.getElementById(button.getAttribute('aria-controls'));
          if (target) {
            target.setAttribute('hidden', '');
            // Remove inline styles when closing
            target.style.display = '';
            target.style.visibility = '';
          }
        });
      }
    });

    if (!window.uswdsPresent) {
      tooltip.on(initialElem);
      table.on(document.body);

      if (!settings.isAdminPage) {
        modal.on(initialElem);
      }
      window.uswdsPresent = true;
    }

    attachAutoModalTriggers(initialElem, settings);
  }
};


